<template>
  <section aria-label="Product Collection" ref="list" class="Collection Collection--Filters page-width">
    <Facets v-if="collection.filters" :facets="collection.filters" :terms="terms" @apply-filters="applyFilters" @reset-cursor="resetCursor" />
    <div class="Collection-GridWrapper">
      <div class="Collection-Grid">
        <template v-if="isEmpty">
          <EmptyCollection :terms="terms" />
        </template>
        <template v-else>
          <component v-for="item in items" :is="conditionalComponent(item)" :key="item" :item="item" :ready="promoReady" :appliedFilters="appliedFilters" :terms="qvTerms" />
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
// Vue core
import { ref, onMounted, watch } from 'vue';

// Utilities
import { fetchStorefront } from '@utils/GraphQLClient';
import { blacklistedVariantKeys } from './Composables/Utilities';
import { pubSub } from '@utils/PubSub';
import { deepClone } from '@utils/performance.js';

// Composables
import { PRODUCTS_FROM_COLLECTION_QUERY } from './Composables/Queries';

//Components
import Facets from './Facets.vue';
import EmptyCollection from './EmptyCollection.vue';
import ContentCard from './ContentCard.vue';
import ProductCard from './ProductCard.vue';

//Props
const props = defineProps({
  handle: String,
  terms: Object,
  qvTerms: Object,
});

// Reactive State
const items = ref([]);
const list = ref(null);
const collection = ref({});
const batchSize = ref(12);
const isEmpty = ref(false);
const promoReady = ref(false);

// Pagination and Fetch State
const observer = ref(null);
const endCursor = ref(null);
const hasNextPage = ref(true);
const isFetching = ref(false);
const appliedFilters = ref([]);
const variantsFetched = ref(1);

/**
 * Stops observing the currently observed DOM element, if any.
 * Useful for resetting the observer before observing a new element.
 */
const unobserve = () => {
  console.log('Unobserving...');
  if (observer.value) observer.value.disconnect();
};

/**
 * Observes the last `.js-product-card` element in the `list`.
 *
 * This function:
 * - First unobserves any currently observed element.
 * - Waits for the current call stack to clear (via `setTimeout` with 0 delay).
 * - Then queries the DOM for the last `.js-product-card` element inside `list.value`.
 * - If found, begins observing it with `observer.value`.
 * - Logs warnings if no matching element is found.
 */
const observeLastCard = () => {
  unobserve();
  setTimeout(() => {
    const lastCard = list.value?.querySelector('.js-product-card:last-child');
    if (lastCard) {
      console.log('Observing last card:', lastCard);
      observer.value.observe(lastCard);
    } else {
      console.warn('No last card found to observe');
    }
  }, 0);
};

/**
 * Applies selected filters to a product collection and fetches filtered products.
 *
 * @async
 * @function
 * @param {Array<Object>} selections - An array of filter selection objects.
 *   Each object contains a single key-value pair representing a selected filter.
 *
 * The function does the following:
 * 1. Maps each selection to a filter input object by matching it against the current collection filters.
 * 2. Handles special logic for filters using Shopify Metaobjects.
 * 3. Updates the reactive state with the new set of filters.
 * 4. Resets pagination and product list states.
 * 5. Triggers a new product fetch with the applied filters.
 */
const applyFilters = async (selections) => {
  const filters = selections.reduce((arr, item) => {
    const data = Object.entries(item)[0]; // Extracts the key-value pair
    const key = data[0];
    const value = data[1];

    // Find the index of the filter group that matches the selection key
    const matchingFacetIndex = collection.value.filters.findIndex((filter) => {
      return filter.id === key;
    });

    // Find the matching facet (filter option) within the group
    const facet = collection.value.filters[matchingFacetIndex].values.find((filter) => {
      const isColor = filter.id.includes('color');

      if (!isColor) return filter.label === value;

      // Handling for filters using Shopify Metaobject GIDs (like color)
      const parsedGraphId = value.split('gid://shopify/Metaobject/')[1].trim();

      return filter.id.includes(parsedGraphId);
    });

    // Parse and add the matched filter input to the array to be used as our filters argument
    arr.push(JSON.parse(facet.input));

    return arr;
  }, []);

  // Update state with new filters and reset pagination
  appliedFilters.value = filters;
  variantsFetched.value = 100;
  isFetching.value = false;
  hasNextPage.value = true;
  endCursor.value = null;
  items.value = [];

  // Fetch products with the new filters
  await fetchProducts();
};

/**
 * Initializes a new IntersectionObserver to load more products when the observed element
 * becomes visible in the viewport.
 *
 * - Disconnects any existing observer to avoid duplicate observations.
 * - Creates a new IntersectionObserver with a threshold of 0.6 (i.e., 60% visibility).
 * - When the observed element intersects the viewport, triggers `fetchProducts()`.
 *
 * This is typically used for infinite scrolling or lazy-loading additional items.
 */
const initializeObserver = () => {
  // If an observer already exists, disconnect it to prevent duplicate callbacks
  if (observer.value) unobserve();
  console.log('Observing...');

  // Create and assign a new IntersectionObserver instance
  observer.value = new IntersectionObserver(
    (entries) => {
      const { isIntersecting } = entries[0];
      console.log('Observer callback triggered, isIntersecting:', isIntersecting);

      // If the target product card is intersecting the viewport, fetch more products
      if (isIntersecting) fetchProducts();
    },
    { threshold: 0.6 } // Trigger when 60% of the target product card is visible
  );
};

const updateNestedFilterValues = (data) => {
  /**
   * Updates each filter object in the array by adding an `applied: false` property
   * to every object inside the `values` array (if `values` exists).
   *
   * @param {Array} data - The input array containing category objects.
   * @returns {Array} - A new array with updated objects, including `applied: false` in `values`.
   */
  return data.map((category) => ({
    ...category,
    values: category.values ? category.values.map((value) => ({ ...value, applied: false })) : category.values,
  }));
};

/**
 * Fetches a batch of products from a Shopify collection using GraphQL.
 *
 * @async
 * @function
 *
 * This function:
 * - Prevents multiple concurrent fetches or fetching beyond the last page.
 * - Constructs and sends a GraphQL query to retrieve products, applying filters and pagination.
 * - Updates the local state with new product data and pagination info.
 * - Initializes filters if they haven't been populated yet.
 * - Formats and stores the retrieved product variants for display.
 * - Handles errors and manages loading state appropriately.
 *
 * @returns {Promise<void|Array>} Returns an empty array on failure, otherwise updates reactive state in place.
 */
const fetchProducts = async () => {
  // Prevent duplicate fetches or unnecessary requests if there's no next page
  if (isFetching.value || !hasNextPage.value) {
    unobserve();
    return;
  }

  const batch = [];
  isFetching.value = true;

  // Ensure collection structure exists
  collection.value = collection.value || {};
  collection.value.title = collection.value?.title || '';
  collection.value.filters = collection.value?.filters || [];
  endCursor.value = endCursor.value || null;
  console.log('Fetching Products...');
  try {
    // Build the GraphQL request payload
    const request = {
      query: PRODUCTS_FROM_COLLECTION_QUERY,
      variables: {
        handle: props.handle,
        first: batchSize.value,
        after: endCursor.value,
        sortKey: 'COLLECTION_DEFAULT',
        filters: appliedFilters.value,
        variantsRetrieved: variantsFetched.value,
      },
    };

    // Send the request to the Storefront API
    const { data } = await fetchStorefront(request);
    const products = data?.collection?.products ?? {};

    // Extract product nodes and append them to the batch
    batch.push(
      ...products.edges.map((edge) => {
        const product = edge.node;
        const variants = product.variants.nodes || [];
        product.variants = variants;
        return product;
      })
    );
    // Update pagination cursor
    endCursor.value = products.pageInfo.endCursor;

    // If filters haven't been initialized yet, populate them
    if (collection.value.filters.length <= 0) {
      collection.value.filters = updateNestedFilterValues(products.filters);
    }

    // Update pagination flag and collection title
    hasNextPage.value = products.pageInfo.hasNextPage;
    collection.value.title = collection.value?.title || data.collection.title;

    // Merge new products with existing list and process variants
    const results = [...items.value, ...batch];
    const values = displayExposedVariants(results);
    items.value = values;

    // Update empty state flag
    isEmpty.value = items.value.length <= 0;
  } catch (error) {
    console.error(`Collection query for ${props.handle} failed.`, error);
    return [];
  } finally {
    // Reset fetching state
    isFetching.value = false;
    console.log('Fetched Products: ', items.value.length);
  }
};

/**
 * Filters and restructures product variants based on applied variant-level facet filters.
 *
 * This function:
 * - Extracts active variant metafield filters from `appliedFilters`.
 * - Groups selected variant values by their metafield keys.
 * - Iterates through each product and finds matching variants that satisfy **all** selected facet conditions.
 * - Deduplicates variants by the "Color" option (if applicable).
 * - Returns a new array of products, each with a single, matching variant.
 *
 * Supports filtering metafield values that are:
 * - Simple strings
 * - Stringified arrays (e.g., "['Blue', 'Red']"), by parsing them into actual arrays.
 *
 * @param {Array<Object>} products - The original list of products with full variant lists.
 * @returns {Array<Object>} A filtered list of products where each includes only the exposed variant(s) matching applied filters.
 */
const displayExposedVariants = (products) => {
  // Get only variant metafield filters that aren't blacklisted
  const variantFacets = appliedFilters.value
    .map((facet) => facet.variantMetafield)
    .filter((facet) => {
      return facet && !blacklistedVariantKeys.includes(facet.key);
    });

  if (variantFacets.length === 0) return products;

  // Group selected facet values by key
  const selectedValuesByKey = variantFacets.reduce((grouped, facet) => {
    if (!grouped[facet.key]) grouped[facet.key] = new Set();
    grouped[facet.key].add(facet.value);
    return grouped;
  }, {});

  const colorSelected = selectedValuesByKey['color_group'];
  const hasColorSelected = !!colorSelected && colorSelected.size > 0;

  // Clone the products to avoid mutation or unitended data issues
  const itemsInCollection = deepClone(products);

  // Filter variants based on selected facet conditions
  const variantMatches = itemsInCollection.reduce((result, product) => {
    const variants = product.variants;

    //Match variants that satisfy ALL variantMetafield conditions
    const matchedVariants = variants.filter((variant) =>
      Object.entries(selectedValuesByKey).every(([key, selectedSet]) => {
        let value = variant[key]?.value;

        if (!value) return false;

        // Handle case where value is a stringified array (a metafield that accepts one or many values)
        let values;
        try {
          const parsed = JSON.parse(value.replace(/'/g, '"'));
          values = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          values = [value]; // Treat as single string if metafield accepts single value
        }

        // Check if any value exists in the selectedSet
        return values.some((v) => selectedSet.has(v));
      })
    );

    // Remove redundancies by color
    const uniqueColorVariants = matchedVariants.filter((current, index, all) => {
      /**
       * @param {Object} current - The current variant being processed in the filter loop.
       * @param {number} index - The index of the current variant in the matchedVariants array.
       * @param {Array<Object>} all - The full array of matchedVariants being filtered.
       *
       * The goal is to keep only the first occurrence of each unique color.
       */

      // Get the color of the current variant
      const currentColor = current.selectedOptions.find((opt) => opt.name === 'Color')?.value;
      return (
        index ===
        all.findIndex((other) => {
          const otherColor = other.selectedOptions.find((opt) => opt.name === 'Color')?.value;
          return currentColor === otherColor;
        })
      );
    });

    // Create a new product per unique matching variant
    const exposedVariants = uniqueColorVariants.map((variant) => {
      const item = deepClone(product);
      item.variants = [variant];
      return item;
    });

    result.push(...exposedVariants);
    return result;
  }, []);

  return variantMatches;
};

const conditionalComponent = (item) => {
  const { type } = item;

  if (type === 'Content') {
    return ContentCard;
  } else {
    return ProductCard;
  }
};

const mountPromo = () => {
  pubSub.unsubscribe('dy:promo:init', mountPromo);
  pubSub.publish('dy:promo:ready');
};

const mountFallback = () => {
  pubSub.unsubscribe('dy:promo:init', mountFallback);
  pubSub.publish('dy:promo:ready');
};

const setPromo = () => {
  pubSub.unsubscribe('dy:promo:ready', setPromo);
  promoReady.value = true;
};

const resetCursor = () => {
  console.log('Resetting Collection Cursor...');
  appliedFilters.value = [];
  variantsFetched.value = 1;
  isFetching.value = false;
  hasNextPage.value = true;
  endCursor.value = null;
  items.value = [];
};

onMounted(async () => {
  pubSub.subscribe('dy:promo:init', mountPromo, mountFallback);
  pubSub.subscribe('dy:promo:ready', setPromo);
  initializeObserver();
  watch(() => items.value, observeLastCard, { flush: 'post' });
  await fetchProducts();
});
</script>
